import { MediaAttachmentDto, AddAttachmentRequest, MediaSearchParams, MediaSearchResult } from '../types/mediaAttachment';
import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

function getCompanyNo(): string {
  const userData = AuthService.getUserDataFromToken();
  if (!userData?.companyNo) {
    throw new Error('Ingen brukerdata tilgjengelig');
  }
  return userData.companyNo;
}

class MediaAttachmentService {

  async getAttachments(entityType: string, entityKey: string): Promise<MediaAttachmentDto[]> {
    const url = `${API_BASE_URL}/${getCompanyNo()}/media/${entityType}/${entityKey}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Kunne ikke hente vedlegg (${response.status})`);
    }

    return response.json();
  }

  async getAttachment(id: number): Promise<MediaAttachmentDto> {
    const url = `${API_BASE_URL}/${getCompanyNo()}/media/${id}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Kunne ikke hente vedlegg (${response.status})`);
    }

    return response.json();
  }

  async addAttachment(request: AddAttachmentRequest): Promise<MediaAttachmentDto> {
    const url = `${API_BASE_URL}/${getCompanyNo()}/media`;
    const response = await AuthService.authenticatedPost(url, request);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Kunne ikke laste opp vedlegg'));
    }

    return response.json();
  }

  async deleteAttachment(id: number): Promise<void> {
    const url = `${API_BASE_URL}/${getCompanyNo()}/media/${id}`;
    const response = await AuthService.authenticatedDelete(url);

    if (!response.ok) {
      throw new Error(`Kunne ikke slette vedlegg (${response.status})`);
    }
  }

  async searchAttachments(params: MediaSearchParams): Promise<MediaSearchResult> {
    const queryParts: string[] = [];
    if (params.entityType) queryParts.push(`entityType=${encodeURIComponent(params.entityType)}`);
    if (params.mediaType) queryParts.push(`mediaType=${encodeURIComponent(params.mediaType)}`);
    if (params.fromDate) queryParts.push(`fromDate=${encodeURIComponent(params.fromDate)}`);
    if (params.toDate) queryParts.push(`toDate=${encodeURIComponent(params.toDate)}`);
    if (params.page) queryParts.push(`page=${params.page}`);
    if (params.pageSize) queryParts.push(`pageSize=${params.pageSize}`);

    const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    const url = `${API_BASE_URL}/${getCompanyNo()}/media/search${queryString}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Kunne ikke soeke vedlegg (${response.status})`);
    }

    return response.json();
  }
}

export const mediaAttachmentService = new MediaAttachmentService();
